// START_MODULE_CONTRACT
//   PURPOSE: Pure helpers для Gantt timeline: календарь, расписание загрузки, строки UI.
//   SCOPE: date ranges, workload schedule (flow-shop), row building, formatting.
//   DEPENDS: frontend/src/utils/dateUtils
//   LINKS: M-FE-TIMELINE, V-M-FE-TIMELINE
// END_MODULE_CONTRACT
//
// START_MODULE_MAP
//   getTimelineViewport - плавающее окно: −N раб. дней … 1-е число след. месяца
//   getTimelineDataRange - диапазон данных (календарь, навигация по месяцам)
//   getVisiblePageDates - даты страницы с обрезкой по viewport на пересекающихся месяцах
//   extendDatesToFillColumnCount - дополняет даты вперёд до заполнения ширины сетки
//   getTimelineColumnCapacity - число колонок дней по доступной ширине и ширине label
//   getTimelineLabelColumnWidth - W или 2W при свёрнутом sidebar (UC-018)
//   TIMELINE_LABEL_COLUMN_WIDTH_EXPANDED - 2× базовая ширина label-столбца
//   addWorkingDays - сдвиг вперёд на N рабочих дней
//   subtractWorkingDays - сдвиг назад на N рабочих дней
//   getTimelineViewportStart - старт viewport от referenceDate
//   getTimelineViewportEnd - конец viewport от referenceDate
//   getEarliestFeatureStartIso - самый ранний старт неархивной фичи
//   computeWorkloadSchedule - flow-shop scheduling engine
//   buildStages - группировка ролей в параллельные этапы
//   getRoleScheduleStart - старт задачи роли с учётом prior-used (UC-015)
//   updateRoleDayUsage - обновление потребления дня роли после задачи (UC-015)
//   getRoleDayCapacity - net-ёмкость роли на ISO-день (UC-021)
//   isRoleAbsentOn - ISO-дата внутри интервала отсутствия (UC-021)
//   buildTimelineRows - строки для Gantt UI
//   filterCompletedTimelineRows - скрытие фич с loadEndIso < today (UC-019)
//   isFeatureOnTimeline - только IN_PROGRESS на Gantt (UC-020)
//   TIMELINE_VIEW_MODE - режимы отображения (детализация / сводка)
//   formatFeatureSpanRange - подпись диапазона дат задачи для сводки
//   isIsoWithinInclusiveSpan - дата внутри [startIso, endIso]
//   overlapsVisibleWindow, isSameTimelineParticipant - visibility helpers
// END_MODULE_MAP
//
// START_CHANGE_SUMMARY
//   LAST_CHANGE: 2026-07-28 - UC-021: экспорт getRoleDayCapacity и isRoleAbsentOn для расчёта доступных часов дня в actual-движке.
//   LAST_CHANGE: 2026-07-28 - UC-020: isFeatureOnTimeline — исключает BACKLOG и ARCHIVED.
//   LAST_CHANGE: 2026-07-28 - UC-019: filterCompletedTimelineRows — скрытие завершённых задач на Gantt.
// END_CHANGE_SUMMARY

import {
  addDays,
  addYears,
  formatDayMonth,
  isWeekend,
  startOfDay,
  toIsoDate,
} from '../../utils/dateUtils';
import { computeNetDayCapacity } from './overheadScheduleUtils';

export const TIMELINE_RANGE_YEARS = 1;
export const TIMELINE_LOOKBACK_WORKING_DAYS = 3;
export const TIMELINE_DAY_COLUMN_WIDTH = 32;
export const TIMELINE_LABEL_COLUMN_WIDTH = 163;
export const TIMELINE_LABEL_COLUMN_WIDTH_EXPANDED = TIMELINE_LABEL_COLUMN_WIDTH * 2;

export const TIMELINE_VIEW_MODE = {
  DETAILED: 'detailed',
  OVERVIEW: 'overview',
};

// START_METHOD_subtractWorkingDays
// START_CONTRACT: subtractWorkingDays
//   PURPOSE: Сдвигает дату назад на N рабочих дней (календарь или weekend fallback).
//   INPUTS: date Date, count number, dayOffSet Set, workingHoursByDate Map.
//   OUTPUTS: Date — начало дня count рабочих дней назад (не включая date).
//   SIDE_EFFECTS: none.
//   LINKS: M-FE-TIMELINE
// END_CONTRACT: subtractWorkingDays
export function subtractWorkingDays(
  date,
  count,
  dayOffSet = new Set(),
  workingHoursByDate = new Map(),
) {
  if (!(count > 0)) {
    return startOfDay(date);
  }
  let cursor = startOfDay(date);
  let remaining = count;
  let guard = 0;
  while (remaining > 0 && guard < 366) {
    guard += 1;
    cursor = addDays(cursor, -1);
    if (!isTimelineDayOff(cursor, dayOffSet, workingHoursByDate)) {
      remaining -= 1;
    }
  }
  return cursor;
}
// END_METHOD_subtractWorkingDays

// START_METHOD_addWorkingDays
// START_CONTRACT: addWorkingDays
//   PURPOSE: Сдвигает дату вперёд на N рабочих дней (календарь или weekend fallback).
//   INPUTS: date Date, count number, dayOffSet Set, workingHoursByDate Map.
//   OUTPUTS: Date — конец дня count рабочих дней вперёд (не включая date при count > 0).
//   SIDE_EFFECTS: none.
//   LINKS: M-FE-TIMELINE
// END_CONTRACT: addWorkingDays
export function addWorkingDays(
  date,
  count,
  dayOffSet = new Set(),
  workingHoursByDate = new Map(),
) {
  if (!(count > 0)) {
    return startOfDay(date);
  }
  let cursor = startOfDay(date);
  let remaining = count;
  let guard = 0;
  while (remaining > 0 && guard < 366) {
    guard += 1;
    cursor = addDays(cursor, 1);
    if (!isTimelineDayOff(cursor, dayOffSet, workingHoursByDate)) {
      remaining -= 1;
    }
  }
  return cursor;
}
// END_METHOD_addWorkingDays

// START_METHOD_getTimelineViewportStart
// START_CONTRACT: getTimelineViewportStart
//   PURPOSE: Плавающий старт viewport: N рабочих дней назад от referenceDate.
//   INPUTS: referenceDate Date, lookbackWorkingDays number, dayOffSet Set, workingHoursByDate Map.
//   OUTPUTS: Date.
//   SIDE_EFFECTS: none.
//   LINKS: M-FE-TIMELINE
// END_CONTRACT: getTimelineViewportStart
export function getTimelineViewportStart(
  referenceDate = new Date(),
  lookbackWorkingDays = TIMELINE_LOOKBACK_WORKING_DAYS,
  dayOffSet = new Set(),
  workingHoursByDate = new Map(),
) {
  return subtractWorkingDays(
    referenceDate,
    lookbackWorkingDays,
    dayOffSet,
    workingHoursByDate,
  );
}
// END_METHOD_getTimelineViewportStart

// START_METHOD_getTimelineViewportEnd
// START_CONTRACT: getTimelineViewportEnd
//   PURPOSE: Плавающий конец viewport: 1-е число месяца, следующего за referenceDate.
//   INPUTS: referenceDate Date.
//   OUTPUTS: Date.
//   SIDE_EFFECTS: none.
//   LINKS: M-FE-TIMELINE
// END_CONTRACT: getTimelineViewportEnd
export function getTimelineViewportEnd(referenceDate = new Date()) {
  const ref = startOfDay(referenceDate);
  return startOfDay(new Date(ref.getFullYear(), ref.getMonth() + 1, 1));
}
// END_METHOD_getTimelineViewportEnd

// START_METHOD_getTimelineViewport
// START_CONTRACT: getTimelineViewport
//   PURPOSE: Плавающее окно: lookback рабочих дней назад … 1-е число следующего месяца.
//   INPUTS: referenceDate, lookbackWorkingDays, dayOffSet, workingHoursByDate.
//   OUTPUTS: { start, end } Date pair.
//   SIDE_EFFECTS: none.
//   LINKS: M-FE-TIMELINE
// END_CONTRACT: getTimelineViewport
export function getTimelineViewport(
  referenceDate = new Date(),
  lookbackWorkingDays = TIMELINE_LOOKBACK_WORKING_DAYS,
  dayOffSet = new Set(),
  workingHoursByDate = new Map(),
) {
  return {
    start: getTimelineViewportStart(
      referenceDate,
      lookbackWorkingDays,
      dayOffSet,
      workingHoursByDate,
    ),
    end: getTimelineViewportEnd(referenceDate),
  };
}
// END_METHOD_getTimelineViewport

// START_METHOD_getTimelineDataRange
// START_CONTRACT: getTimelineDataRange
//   PURPOSE: Диапазон загрузки календаря и навигации: от viewportStart до +1 год от referenceDate.
//   INPUTS: referenceDate, lookbackWorkingDays, dayOffSet, workingHoursByDate.
//   OUTPUTS: { start, end } Date pair.
//   SIDE_EFFECTS: none.
//   LINKS: M-FE-TIMELINE
// END_CONTRACT: getTimelineDataRange
export function getTimelineDataRange(
  referenceDate = new Date(),
  lookbackWorkingDays = TIMELINE_LOOKBACK_WORKING_DAYS,
  dayOffSet = new Set(),
  workingHoursByDate = new Map(),
) {
  const start = getTimelineViewportStart(
    referenceDate,
    lookbackWorkingDays,
    dayOffSet,
    workingHoursByDate,
  );
  const end = addYears(startOfDay(referenceDate), TIMELINE_RANGE_YEARS);
  return { start, end };
}
// END_METHOD_getTimelineDataRange

// START_METHOD_getTimelineRange
// START_CONTRACT: getTimelineRange
//   PURPOSE: Алиас getTimelineViewport — плавающее окно вокруг referenceDate.
//   INPUTS: referenceDate optional Date; lookbackWorkingDays number; dayOffSet Set; workingHoursByDate Map.
//   OUTPUTS: { start, end } Date pair.
//   SIDE_EFFECTS: none.
//   LINKS: M-FE-TIMELINE
// END_CONTRACT: getTimelineRange
export function getTimelineRange(
  referenceDate = new Date(),
  lookbackWorkingDays = TIMELINE_LOOKBACK_WORKING_DAYS,
  dayOffSet = new Set(),
  workingHoursByDate = new Map(),
) {
  return getTimelineViewport(
    referenceDate,
    lookbackWorkingDays,
    dayOffSet,
    workingHoursByDate,
  );
}
// END_METHOD_getTimelineRange

// START_METHOD_parseIsoDateLocal
// START_CONTRACT: parseIsoDateLocal
//   PURPOSE: Разобрать 'YYYY-MM-DD' в локальную Date без сдвига часового пояса.
//   INPUTS: iso string|null.
//   OUTPUTS: Date|null.
//   SIDE_EFFECTS: none.
//   LINKS: M-FE-TIMELINE
// END_CONTRACT: parseIsoDateLocal
export function parseIsoDateLocal(iso) {
  if (!iso || typeof iso !== 'string') {
    return null;
  }
  const [year, month, day] = iso.split('-').map(Number);
  if (!year || !month || !day) {
    return null;
  }
  return new Date(year, month - 1, day);
}
// END_METHOD_parseIsoDateLocal

// START_METHOD_isFeatureOnTimeline
// START_CONTRACT: isFeatureOnTimeline
//   PURPOSE: Фича попадает на Gantt только в статусе IN_PROGRESS (не BACKLOG, не ARCHIVED).
//   INPUTS: feature (Feature|plain object).
//   OUTPUTS: boolean.
//   SIDE_EFFECTS: none.
//   LINKS: UC-020, V-M-FE-TIMELINE
// END_CONTRACT: isFeatureOnTimeline
export function isFeatureOnTimeline(feature) {
  if (!feature) {
    return false;
  }
  if (typeof feature.isOnTimeline === 'boolean') {
    return feature.isOnTimeline;
  }
  if (feature.status === 'BACKLOG' || feature.status === 'ARCHIVED') {
    return false;
  }
  if (feature.isArchived || feature.isBacklog) {
    return false;
  }
  if (feature.status === 'IN_PROGRESS') {
    return true;
  }
  return !feature.isArchived;
}
// END_METHOD_isFeatureOnTimeline

// START_METHOD_getEarliestFeatureStartIso
// START_CONTRACT: getEarliestFeatureStartIso
//   PURPOSE: Найти самую раннюю дату старта среди фич на таймлайне (IN_PROGRESS).
//   INPUTS: features Feature[].
//   OUTPUTS: string|null - ISO 'YYYY-MM-DD' или null, если стартов нет.
//   SIDE_EFFECTS: none.
//   LINKS: M-FE-TIMELINE, UC-020
// END_CONTRACT: getEarliestFeatureStartIso
export function getEarliestFeatureStartIso(features = []) {
  let earliest = null;
  for (const feature of features) {
    if (!isFeatureOnTimeline(feature) || !feature.startDate) {
      continue;
    }
    if (earliest === null || feature.startDate < earliest) {
      earliest = feature.startDate;
    }
  }
  return earliest;
}
// END_METHOD_getEarliestFeatureStartIso

const DEFAULT_DAY_HOURS = 8;
const SCHEDULE_GUARD_DAYS = 5000;

// START_METHOD_buildMonthPages
// START_CONTRACT: buildMonthPages
//   PURPOSE: Список {year, month} страниц между start и end.
//   INPUTS: start Date, end Date.
//   OUTPUTS: array of page descriptors.
//   SIDE_EFFECTS: none.
//   LINKS: M-FE-TIMELINE
// END_CONTRACT: buildMonthPages
export function buildMonthPages(start, end) {
  const pages = [];
  let cursor = new Date(start.getFullYear(), start.getMonth(), 1);
  const last = new Date(end.getFullYear(), end.getMonth(), 1);
  while (cursor <= last) {
    pages.push({ year: cursor.getFullYear(), month: cursor.getMonth() });
    cursor = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1);
  }
  return pages;
}
// END_METHOD_buildMonthPages

// START_METHOD_getPageDates
// START_CONTRACT: getPageDates
//   PURPOSE: Даты одного месяца в пределах timeline range.
//   INPUTS: page {year, month}, start, end.
//   OUTPUTS: Date[].
//   SIDE_EFFECTS: none.
//   LINKS: M-FE-TIMELINE
// END_CONTRACT: getPageDates
export function getPageDates(page, start, end) {
  if (!page) {
    return [];
  }
  const dates = [];
  const monthEnd = new Date(page.year, page.month + 1, 0);
  let cursor = new Date(page.year, page.month, 1);
  while (cursor <= monthEnd) {
    if (cursor >= start && cursor <= end) {
      dates.push(new Date(cursor));
    }
    cursor = addDays(cursor, 1);
  }
  return dates;
}
// END_METHOD_getPageDates

// START_METHOD_buildInclusiveDateRange
// START_CONTRACT: buildInclusiveDateRange
//   PURPOSE: Непрерывный список дат [rangeStart, rangeEnd] в пределах dataRange.
//   INPUTS: rangeStart Date, rangeEnd Date, dataRange {start, end}.
//   OUTPUTS: Date[].
//   SIDE_EFFECTS: none.
//   LINKS: M-FE-TIMELINE
// END_CONTRACT: buildInclusiveDateRange
export function buildInclusiveDateRange(rangeStart, rangeEnd, dataRange) {
  const dates = [];
  let cursor = startOfDay(rangeStart);
  const end = startOfDay(rangeEnd);
  while (cursor <= end) {
    if (cursor >= dataRange.start && cursor <= dataRange.end) {
      dates.push(new Date(cursor));
    }
    cursor = addDays(cursor, 1);
  }
  return dates;
}
// END_METHOD_buildInclusiveDateRange

// START_METHOD_pageContainsDate
// START_CONTRACT: pageContainsDate
//   PURPOSE: Проверяет, попадает ли дата в календарный месяц страницы.
//   INPUTS: page {year, month}, date Date.
//   OUTPUTS: boolean.
//   SIDE_EFFECTS: none.
//   LINKS: M-FE-TIMELINE
// END_CONTRACT: pageContainsDate
export function pageContainsDate(page, date) {
  if (!page || !date) {
    return false;
  }
  return page.year === date.getFullYear() && page.month === date.getMonth();
}
// END_METHOD_pageContainsDate

// START_METHOD_getVisiblePageDates
// START_CONTRACT: getVisiblePageDates
//   PURPOSE: Даты для страницы месяца; на anchor-месяце — полный viewport (может пересекать месяцы).
//   INPUTS: page, viewport, dataRange, anchorDate optional Date (обычно сегодня).
//   OUTPUTS: Date[].
//   SIDE_EFFECTS: none.
//   LINKS: M-FE-TIMELINE
// END_CONTRACT: getVisiblePageDates
export function getVisiblePageDates(page, viewport, dataRange, anchorDate = null) {
  if (!page || !viewport || !dataRange) {
    return [];
  }

  if (anchorDate && pageContainsDate(page, anchorDate)) {
    return buildInclusiveDateRange(viewport.start, viewport.end, dataRange);
  }

  const dates = getPageDates(page, dataRange.start, dataRange.end);
  const pageStart = startOfDay(new Date(page.year, page.month, 1));
  const pageEnd = startOfDay(new Date(page.year, page.month + 1, 0));

  if (viewport.end < pageStart || viewport.start > pageEnd) {
    return dates;
  }

  const clipStart = viewport.start > pageStart ? viewport.start : pageStart;
  const clipEnd = viewport.end < pageEnd ? viewport.end : pageEnd;

  return dates.filter((date) => date >= clipStart && date <= clipEnd);
}
// END_METHOD_getVisiblePageDates

// START_METHOD_getTimelineLabelColumnWidth
// START_CONTRACT: getTimelineLabelColumnWidth
//   PURPOSE: Ширина sticky-столбца названий фич: W или 2W при свёрнутом sidebar (UC-018).
//   INPUTS: sidebarCollapsed boolean.
//   OUTPUTS: number — TIMELINE_LABEL_COLUMN_WIDTH или TIMELINE_LABEL_COLUMN_WIDTH_EXPANDED.
//   SIDE_EFFECTS: none.
//   LINKS: M-FE-TIMELINE, UC-018
// END_CONTRACT: getTimelineLabelColumnWidth
export function getTimelineLabelColumnWidth(sidebarCollapsed) {
  return sidebarCollapsed
    ? TIMELINE_LABEL_COLUMN_WIDTH_EXPANDED
    : TIMELINE_LABEL_COLUMN_WIDTH;
}
// END_METHOD_getTimelineLabelColumnWidth

// START_METHOD_getTimelineColumnCapacity
// START_CONTRACT: getTimelineColumnCapacity
//   PURPOSE: Сколько колонок дней помещается в availableWidth при заданной ширине label-столбца.
//   INPUTS: availableWidth number, labelWidth number (default TIMELINE_LABEL_COLUMN_WIDTH).
//   OUTPUTS: number >= 0.
//   SIDE_EFFECTS: none.
//   LINKS: M-FE-TIMELINE, UC-018
// END_CONTRACT: getTimelineColumnCapacity
export function getTimelineColumnCapacity(
  availableWidth,
  labelWidth = TIMELINE_LABEL_COLUMN_WIDTH,
) {
  if (!(availableWidth > 0)) {
    return 0;
  }
  const dayArea = availableWidth - labelWidth;
  if (dayArea <= 0) {
    return 0;
  }
  return Math.floor(dayArea / TIMELINE_DAY_COLUMN_WIDTH);
}
// END_METHOD_getTimelineColumnCapacity

// START_METHOD_extendDatesToFillColumnCount
// START_CONTRACT: extendDatesToFillColumnCount
//   PURPOSE: Дополняет список дат вперёд до targetColumnCount (с учётом hideNonWorkingDays), не выходя за dataRangeEnd.
//   INPUTS: dates Date[], targetColumnCount number, dataRangeEnd Date, dayOffSet, workingHoursByDate, hideNonWorkingDays.
//   OUTPUTS: Date[].
//   SIDE_EFFECTS: none.
//   LINKS: M-FE-TIMELINE
// END_CONTRACT: extendDatesToFillColumnCount
export function extendDatesToFillColumnCount(
  dates,
  targetColumnCount,
  dataRangeEnd,
  dayOffSet,
  workingHoursByDate,
  hideNonWorkingDays,
) {
  if (!dates?.length) {
    return [];
  }
  if (!(targetColumnCount > 0)) {
    return hideNonWorkingDays
      ? filterVisibleDates(dates, dayOffSet, workingHoursByDate, true)
      : dates.map((date) => new Date(date));
  }

  const working = dates.map((date) => new Date(date));
  const toDisplay = () => (
    hideNonWorkingDays
      ? filterVisibleDates(working, dayOffSet, workingHoursByDate, true)
      : working
  );

  if (toDisplay().length >= targetColumnCount) {
    return toDisplay();
  }

  let cursor = startOfDay(working[working.length - 1]);
  const endLimit = startOfDay(dataRangeEnd);
  let guard = 0;

  while (toDisplay().length < targetColumnCount && guard < 800) {
    guard += 1;
    cursor = addDays(cursor, 1);
    if (cursor > endLimit) {
      break;
    }
    working.push(new Date(cursor));
  }

  return toDisplay();
}
// END_METHOD_extendDatesToFillColumnCount

// START_METHOD_isTimelineDayOff
// START_CONTRACT: isTimelineDayOff
//   PURPOSE: Определяет нерабочий день по calendar map или weekend fallback.
//   INPUTS: date, dayOffSet, workingHoursByDate.
//   OUTPUTS: boolean.
//   SIDE_EFFECTS: none.
//   LINKS: M-FE-TIMELINE
// END_CONTRACT: isTimelineDayOff
export function isTimelineDayOff(date, dayOffSet, workingHoursByDate) {
  const hasCalendar = dayOffSet.size > 0 || workingHoursByDate.size > 0;
  const iso = toIsoDate(date);
  return hasCalendar ? dayOffSet.has(iso) : isWeekend(date);
}
// END_METHOD_isTimelineDayOff

// START_METHOD_filterVisibleDates
// START_CONTRACT: filterVisibleDates
//   PURPOSE: Фильтрует даты при hideNonWorkingDays.
//   INPUTS: dates[], dayOffSet, workingHoursByDate, hideNonWorkingDays flag.
//   OUTPUTS: filtered Date[].
//   SIDE_EFFECTS: none.
//   LINKS: M-FE-TIMELINE
// END_CONTRACT: filterVisibleDates
export function filterVisibleDates(dates, dayOffSet, workingHoursByDate, hideNonWorkingDays) {
  if (!hideNonWorkingDays) {
    return dates;
  }
  return dates.filter(
    (date) => !isTimelineDayOff(date, dayOffSet, workingHoursByDate),
  );
}
// END_METHOD_filterVisibleDates

// START_METHOD_buildDayOffSet
// START_CONTRACT: buildDayOffSet
//   PURPOSE: Set ISO dates с 0 working hours из массива hours.
//   INPUTS: workingHours array, start Date.
//   OUTPUTS: Set<string> iso dates.
//   SIDE_EFFECTS: none.
//   LINKS: M-FE-TIMELINE
// END_CONTRACT: buildDayOffSet
export function buildDayOffSet(workingHours, start) {
  const set = new Set();
  if (!Array.isArray(workingHours)) {
    return set;
  }
  workingHours.forEach((hours, index) => {
    if (hours === 0) {
      set.add(toIsoDate(addDays(start, index)));
    }
  });
  return set;
}
// END_METHOD_buildDayOffSet

// START_METHOD_buildWorkingHoursByDate
// START_CONTRACT: buildWorkingHoursByDate
//   PURPOSE: Map iso → hours из массива workingHours.
//   INPUTS: workingHours array, start Date.
//   OUTPUTS: Map<string, number>.
//   SIDE_EFFECTS: none.
//   LINKS: M-FE-TIMELINE
// END_CONTRACT: buildWorkingHoursByDate
export function buildWorkingHoursByDate(workingHours, start) {
  const map = new Map();
  if (!Array.isArray(workingHours)) {
    return map;
  }
  workingHours.forEach((hours, index) => {
    map.set(toIsoDate(addDays(start, index)), hours);
  });
  return map;
}
// END_METHOD_buildWorkingHoursByDate

// START_METHOD_getAbsenceForDate
// START_CONTRACT: getAbsenceForDate
//   PURPOSE: Находит absence covering date.
//   INPUTS: date, absences array.
//   OUTPUTS: absence object or null.
//   SIDE_EFFECTS: none.
//   LINKS: M-FE-TIMELINE
// END_CONTRACT: getAbsenceForDate
export function getAbsenceForDate(date, absences) {
  if (!absences?.length) {
    return null;
  }
  const iso = toIsoDate(date);
  return absences.find((absence) => iso >= absence.from && iso <= absence.to) ?? null;
}
// END_METHOD_getAbsenceForDate

// START_METHOD_rangesOverlap
// START_CONTRACT: rangesOverlap
//   PURPOSE: Проверяет пересечение двух ISO date ranges.
//   INPUTS: aFrom, aTo, bFrom, bTo ISO strings.
//   OUTPUTS: boolean.
//   SIDE_EFFECTS: none.
//   LINKS: M-FE-TIMELINE
// END_CONTRACT: rangesOverlap
export function rangesOverlap(aFrom, aTo, bFrom, bTo) {
  return aFrom <= bTo && aTo >= bFrom;
}
// END_METHOD_rangesOverlap

// START_METHOD_hasAbsenceOverlap
// START_CONTRACT: hasAbsenceOverlap
//   PURPOSE: Проверяет overlap candidate absence с others.
//   INPUTS: candidate {from,to}, others array.
//   OUTPUTS: boolean.
//   SIDE_EFFECTS: none.
//   LINKS: M-FE-TIMELINE
// END_CONTRACT: hasAbsenceOverlap
export function hasAbsenceOverlap(candidate, others) {
  return others.some((absence) => rangesOverlap(
    candidate.from,
    candidate.to,
    absence.from,
    absence.to,
  ));
}
// END_METHOD_hasAbsenceOverlap

// START_METHOD_getDayHours
// START_CONTRACT: getDayHours
//   PURPOSE: Hours capacity for ISO date from map or weekend default.
//   INPUTS: iso, date, workingHoursByDate.
//   OUTPUTS: number hours.
//   SIDE_EFFECTS: none.
//   LINKS: M-FE-TIMELINE
// END_CONTRACT: getDayHours
function getDayHours(iso, date, workingHoursByDate) {
  if (workingHoursByDate.has(iso)) {
    return workingHoursByDate.get(iso);
  }
  return isWeekend(date) ? 0 : DEFAULT_DAY_HOURS;
}
// END_METHOD_getDayHours

// START_METHOD_isRoleAbsentOn
// START_CONTRACT: isRoleAbsentOn
//   PURPOSE: Проверяет, попадает ли ISO дата в один из интервалов отсутствия.
//   INPUTS: iso string, absences array.
//   OUTPUTS: boolean.
//   SIDE_EFFECTS: none.
//   LINKS: M-FE-TIMELINE, UC-021
// END_CONTRACT: isRoleAbsentOn
export function isRoleAbsentOn(iso, absences) {
  if (!absences?.length) {
    return false;
  }
  return absences.some((absence) => iso >= absence.from && iso <= absence.to);
}
// END_METHOD_isRoleAbsentOn

// START_METHOD_getRoleDayCapacity
// START_CONTRACT: getRoleDayCapacity
//   PURPOSE: Net-ёмкость роли на один ISO-день: рабочие часы × workload% − overhead.
//   INPUTS: iso string, roleId number, workloadPercent number, overheads array, workingHoursByDate Map.
//   OUTPUTS: number >= 0 (0 для выходного и при нулевой загрузке).
//   SIDE_EFFECTS: none.
//   LINKS: M-FE-TIMELINE, M-FE-OVERHEAD-SCHEDULE, UC-021
// END_CONTRACT: getRoleDayCapacity
export function getRoleDayCapacity(
  iso,
  roleId,
  workloadPercent,
  overheads = [],
  workingHoursByDate = new Map(),
) {
  const date = parseIsoDateLocal(iso);
  if (!date) {
    return 0;
  }
  const factor = (workloadPercent ?? 100) / 100;
  if (!(factor > 0)) {
    return 0;
  }
  const grossDayHours = getDayHours(iso, date, workingHoursByDate ?? new Map()) * factor;
  return computeNetDayCapacity(grossDayHours, roleId, iso, overheads);
}
// END_METHOD_getRoleDayCapacity

// START_METHOD_parseIsoDate
// START_CONTRACT: parseIsoDate
//   PURPOSE: Парсит ISO-строку (YYYY-MM-DD) в локальную Date без сдвига таймзоны.
//   INPUTS: iso string.
//   OUTPUTS: Date.
//   SIDE_EFFECTS: none.
//   LINKS: M-FE-TIMELINE
// END_CONTRACT: parseIsoDate
function parseIsoDate(iso) {
  const [year, month, day] = iso.split('-').map(Number);
  return new Date(year, month - 1, day);
}
// END_METHOD_parseIsoDate

// START_METHOD_maxDate
// START_CONTRACT: maxDate
//   PURPOSE: Возвращает большую из двух дат, корректно обрабатывая null.
//   INPUTS: a Date|null, b Date|null.
//   OUTPUTS: Date|null.
//   SIDE_EFFECTS: none.
//   LINKS: M-FE-TIMELINE
// END_CONTRACT: maxDate
function maxDate(a, b) {
  if (!a) return b ?? null;
  if (!b) return a;
  return a >= b ? a : b;
}
// END_METHOD_maxDate

// START_METHOD_minDate
// START_CONTRACT: minDate
//   PURPOSE: Возвращает меньшую из двух дат, корректно обрабатывая null.
//   INPUTS: a Date|null, b Date|null.
//   OUTPUTS: Date|null.
//   SIDE_EFFECTS: none.
//   LINKS: M-FE-TIMELINE
// END_CONTRACT: minDate
function minDate(a, b) {
  if (!a) return b ?? null;
  if (!b) return a;
  return a <= b ? a : b;
}
// END_METHOD_minDate

/**
 * Планирует задачу по рабочим дням: последовательно «съедает» ёмкость
 * (рабочие часы дня × процент загрузки роли − overhead), пропуская выходные,
 * праздники и дни отсутствия. На стартовой дате может учитывать уже израсходованные
 * часы (usedHoursOnStartDate) при цепочке задач в один день (UC-015).
 */
// START_METHOD_scheduleTaskDays
// START_CONTRACT: scheduleTaskDays
//   PURPOSE: Планирует задачу по ёмкости дней; на стартовой дате учитывает prior-used часы (UC-015).
//   INPUTS: startDate Date, hours number, workloadPercent number, roleId number, overheads array, absences array, workingHoursByDate Map, usedHoursOnStartDate optional number.
//   OUTPUTS: { start, end, hoursByDate } | null.
//   SIDE_EFFECTS: none (pure computation).
//   LINKS: V-M-FE-TIMELINE, M-FE-OVERHEAD-SCHEDULE, UC-015
// END_CONTRACT: scheduleTaskDays
export function scheduleTaskDays(
  startDate,
  hours,
  workloadPercent,
  roleId,
  overheads,
  absences,
  workingHoursByDate,
  usedHoursOnStartDate = 0,
) {
  if (!(hours > 0) || !(workloadPercent > 0)) {
    return null;
  }
  const factor = workloadPercent / 100;
  let remaining = hours;
  let cursor = startOfDay(startDate);
  const startIso = toIsoDate(cursor);
  let firstDay = null;
  let lastDay = null;
  const hoursByDate = new Map();
  let guard = 0;

  while (remaining > 0 && guard < SCHEDULE_GUARD_DAYS) {
    guard += 1;
    const iso = toIsoDate(cursor);
    const grossDayHours = getDayHours(iso, cursor, workingHoursByDate) * factor;
    const capacity = computeNetDayCapacity(grossDayHours, roleId, iso, overheads);
    if (capacity > 0 && !isRoleAbsentOn(iso, absences)) {
      const priorUsed = firstDay === null && iso === startIso ? usedHoursOnStartDate : 0;
      const available = capacity - priorUsed;
      if (available > 0) {
        const dayHours = Math.min(remaining, available);
        if (!firstDay) {
          firstDay = new Date(cursor);
        }
        lastDay = new Date(cursor);
        hoursByDate.set(iso, dayHours);
        remaining -= dayHours;
      }
    }
    cursor = addDays(cursor, 1);
  }

  return firstDay ? { start: firstDay, end: lastDay, hoursByDate } : null;
}
// END_METHOD_scheduleTaskDays

// START_METHOD_getRoleScheduleStart
// START_CONTRACT: getRoleScheduleStart
//   PURPOSE: Определяет дату старта и prior-used часы для следующей задачи роли (UC-015).
//   INPUTS: roleDayUsage Map, roleId, floorDate Date.
//   OUTPUTS: { start Date, usedOnStart number }.
//   SIDE_EFFECTS: none.
//   LINKS: V-M-FE-TIMELINE, UC-015
// END_CONTRACT: getRoleScheduleStart
export function getRoleScheduleStart(roleDayUsage, roleId, floorDate) {
  const usage = roleDayUsage.get(roleId);
  if (!usage) {
    return { start: floorDate, usedOnStart: 0 };
  }
  const start = maxDate(floorDate, usage.date);
  const startIso = toIsoDate(start);
  const usageIso = toIsoDate(usage.date);
  const usedOnStart = startIso === usageIso ? usage.usedHours : 0;
  return { start, usedOnStart };
}
// END_METHOD_getRoleScheduleStart

// START_METHOD_updateRoleDayUsage
// START_CONTRACT: updateRoleDayUsage
//   PURPOSE: Обновляет потребление дня роли после планирования задачи (UC-015).
//   INPUTS: roleDayUsage Map, roleId, span, usedOnStart, workloadPercent, overheads, workingHoursByDate.
//   OUTPUTS: void (mutates roleDayUsage).
//   SIDE_EFFECTS: updates roleDayUsage entry.
//   LINKS: V-M-FE-TIMELINE, UC-015
// END_CONTRACT: updateRoleDayUsage
export function updateRoleDayUsage(
  roleDayUsage,
  roleId,
  span,
  usedOnStart,
  workloadPercent,
  overheads,
  workingHoursByDate,
) {
  const lastIso = toIsoDate(span.end);
  const factor = workloadPercent / 100;
  const grossDayHours = getDayHours(lastIso, span.end, workingHoursByDate) * factor;
  const capacity = computeNetDayCapacity(grossDayHours, roleId, lastIso, overheads);
  const hoursOnLast = span.hoursByDate.get(lastIso) ?? 0;
  const startIso = toIsoDate(span.start);
  const totalUsedOnLast = hoursOnLast + (startIso === lastIso ? usedOnStart : 0);

  if (totalUsedOnLast >= capacity) {
    roleDayUsage.set(roleId, { date: addDays(span.end, 1), usedHours: 0 });
  } else {
    roleDayUsage.set(roleId, { date: new Date(span.end), usedHours: totalUsedOnLast });
  }
}
// END_METHOD_updateRoleDayUsage

// START_METHOD_isConcurrent
// START_CONTRACT: isConcurrent
//   PURPOSE: Проверяет, допускают ли две роли совместную (параллельную) работу.
//   INPUTS: roleById Map, roleIdA, roleIdB.
//   OUTPUTS: boolean.
//   SIDE_EFFECTS: none.
//   LINKS: M-FE-TIMELINE
// END_CONTRACT: isConcurrent
function isConcurrent(roleById, roleIdA, roleIdB) {
  const roleA = roleById.get(roleIdA);
  const roleB = roleById.get(roleIdB);
  return Boolean(
    roleA?.concurrentRoleIds?.includes(roleIdB)
    || roleB?.concurrentRoleIds?.includes(roleIdA),
  );
}
// END_METHOD_isConcurrent

/**
 * Группирует роли фичи (отсортированные по sortOrder) в этапы: соседние роли,
 * взаимно допускающие совместную работу, попадают в один параллельный этап.
 */
// START_METHOD_buildStages
// START_CONTRACT: buildStages
//   PURPOSE: Группирует отсортированные роли в этапы: соседние совместимые роли — в один параллельный этап.
//   INPUTS: roleIds array, roleById Map.
//   OUTPUTS: array of stage arrays (roleId[][]).
//   SIDE_EFFECTS: none.
//   LINKS: V-M-FE-TIMELINE
// END_CONTRACT: buildStages
export function buildStages(roleIds, roleById) {
  const stages = [];
  roleIds.forEach((roleId) => {
    const current = stages[stages.length - 1];
    if (current && current.every((other) => isConcurrent(roleById, roleId, other))) {
      current.push(roleId);
    } else {
      stages.push([roleId]);
    }
  });
  return stages;
}
// END_METHOD_buildStages

/**
 * Рассчитывает «на лету» загрузку сотрудников по фичам и ролям (без сохранения в БД).
 *
 * Модель — конвейер (flow-shop):
 * - фичи обрабатываются в порядке sortOrder;
 * - внутри фичи роли идут этапами по sortOrder, совместные роли — параллельно;
 * - каждая роль (ресурс) выполняет свои задачи по очереди, без двойной загрузки
 *   (при конфликте задача сдвигается вперёд);
 * - остаток net-времени рабочего дня утилизируется следующей задачей той же роли (UC-015);
 * - якорь расписания — старт первой по сортировке фичи (её дата старта, иначе дата
 *   создания);
 * - дата старта фичи — нижняя граница для каждой роли: если она раньше/равна моменту,
 *   когда роль освобождается по конвейеру, роль идёт по конвейеру и не простаивает;
 *   если дата старта позже — задачи фичи отсчитываются от неё.
 *
 * @returns {{ taskSpans: Map<string, {startIso, endIso}>, featureSpans: Map<number, {startIso, endIso}> }}
 */
// START_METHOD_computeWorkloadSchedule
// START_CONTRACT: computeWorkloadSchedule
//   PURPOSE: Flow-shop scheduling: taskSpans и featureSpans для Gantt.
//   INPUTS: features, employees, projectRoles, workingHoursByDate Map, overheads array.
//   OUTPUTS: { taskSpans, featureSpans } Maps.
//   SIDE_EFFECTS: none (pure computation).
//   LINKS: V-M-FE-TIMELINE, M-FE-OVERHEAD-SCHEDULE
// END_CONTRACT: computeWorkloadSchedule
export function computeWorkloadSchedule(
  features,
  employees,
  projectRoles,
  workingHoursByDate,
  overheads = [],
) {
  const taskSpans = new Map();
  const featureSpans = new Map();

  const scheduled = (features ?? [])
    .filter((feature) => isFeatureOnTimeline(feature))
    .slice()
    .sort((a, b) => a.sortOrder - b.sortOrder);

  if (!scheduled.length) {
    return { taskSpans, featureSpans };
  }

  const roleById = new Map((projectRoles ?? []).map((role) => [role.id, role]));
  const employeeByRoleId = new Map(
    (employees ?? []).map((employee) => [employee.projectRoleId, employee]),
  );

  const firstFeature = scheduled[0];
  let baseline;
  if (firstFeature.startDate) {
    baseline = parseIsoDate(firstFeature.startDate);
  } else if (firstFeature.createdAt) {
    baseline = startOfDay(firstFeature.createdAt);
  } else {
    baseline = startOfDay(new Date());
  }

  const roleDayUsage = new Map();

  scheduled.forEach((feature) => {
    // Дата старта фичи — нижняя граница для каждой роли: при отсчёте старта задачи
    // берём максимум из (дата старта фичи) и (конвейерная готовность роли). Если дата
    // старта раньше/равна готовности — роль не простаивает; если позже — отсчёт от неё.
    const featureFloor = feature.startDate ? parseIsoDate(feature.startDate) : baseline;

    const roleIds = feature.estimates
      .filter((estimate) => estimate.estimate > 0)
      .map((estimate) => estimate.projectRoleId)
      .sort((a, b) => (roleById.get(a)?.sortOrder ?? 0) - (roleById.get(b)?.sortOrder ?? 0));

    const stages = buildStages(roleIds, roleById);

    let stagePrevEnd = null;
    let featureStart = null;
    let featureEnd = null;

    stages.forEach((stage) => {
      const stageFloor = stagePrevEnd ? addDays(stagePrevEnd, 1) : featureFloor;
      let stageEnd = null;

      stage.forEach((roleId) => {
        const estimate = feature.estimates.find((item) => item.projectRoleId === roleId);
        const role = roleById.get(roleId);
        const workloadPercent = role?.workloadPercent ?? 100;
        const absences = employeeByRoleId.get(roleId)?.absences ?? [];

        const { start, usedOnStart } = getRoleScheduleStart(roleDayUsage, roleId, stageFloor);
        const span = scheduleTaskDays(
          start,
          estimate.estimate,
          workloadPercent,
          roleId,
          overheads,
          absences,
          workingHoursByDate,
          usedOnStart,
        );
        if (!span) {
          return;
        }

        taskSpans.set(`${feature.id}__${roleId}`, {
          startIso: toIsoDate(span.start),
          endIso: toIsoDate(span.end),
          hoursByDate: span.hoursByDate,
        });

        updateRoleDayUsage(
          roleDayUsage,
          roleId,
          span,
          usedOnStart,
          workloadPercent,
          overheads,
          workingHoursByDate,
        );
        stageEnd = maxDate(stageEnd, span.end);
        featureStart = minDate(featureStart, span.start);
        featureEnd = maxDate(featureEnd, span.end);
      });

      if (stageEnd) {
        stagePrevEnd = stageEnd;
      }
    });

    if (featureStart && featureEnd) {
      featureSpans.set(feature.id, {
        startIso: toIsoDate(featureStart),
        endIso: toIsoDate(featureEnd),
      });
    }
  });

  return { taskSpans, featureSpans };
}
// END_METHOD_computeWorkloadSchedule

// START_METHOD_buildTimelineRows
// START_CONTRACT: buildTimelineRows
//   PURPOSE: Строит nested rows для Gantt из features и schedule.
//   INPUTS: features, employees, projectRoles, workingHoursByDate.
//   OUTPUTS: array of timeline row objects.
//   SIDE_EFFECTS: none.
//   LINKS: M-FE-TIMELINE
// END_CONTRACT: buildTimelineRows
export function buildTimelineRows(
  features,
  employees,
  projectRoles = [],
  workingHoursByDate = new Map(),
  overheads = [],
  actualHours = [],
  workloadSchedule = null,
) {
  const employeeByRoleId = new Map(
    employees.map((employee) => [employee.projectRoleId, employee]),
  );
  const roleById = new Map(projectRoles.map((role) => [role.id, role]));
  const { taskSpans, featureSpans } = workloadSchedule ?? computeWorkloadSchedule(
    features,
    employees,
    projectRoles,
    workingHoursByDate,
    overheads,
  );

  return features
    .filter((feature) => isFeatureOnTimeline(feature))
    .slice()
    .sort((a, b) => a.sortOrder - b.sortOrder)
    .map((feature) => {
      const employeeRows = feature.estimates
        .slice()
        .sort((a, b) => (
          (roleById.get(a.projectRoleId)?.sortOrder ?? 0)
          - (roleById.get(b.projectRoleId)?.sortOrder ?? 0)
        ))
        .map((estimate) => {
          const employee = employeeByRoleId.get(estimate.projectRoleId);
          const name = employee?.name
            || estimate.projectRoleName
            || estimate.projectRoleShortName
            || 'Без исполнителя';
          const span = taskSpans.get(`${feature.id}__${estimate.projectRoleId}`) ?? null;
          const effectiveTotal = span?.hoursByDate
            ? [...span.hoursByDate.values()].reduce((sum, h) => sum + h, 0)
            : 0;
          const hasStoredActuals = (actualHours ?? []).some(
            (item) => item.featureId === feature.id && item.projectRoleId === estimate.projectRoleId,
          );
          return {
            key: `${feature.id}-${estimate.projectRoleId}`,
            name,
            employeeId: employee?.id ?? null,
            projectRoleId: estimate.projectRoleId,
            featureId: feature.id,
            roleShortName: estimate.projectRoleShortName || estimate.projectRoleName,
            hours: estimate.estimate,
            effectiveTotal,
            hasStoredActuals,
            assigned: Boolean(employee),
            absences: employee?.absences ?? [],
            loadStartIso: span?.startIso ?? null,
            loadEndIso: span?.endIso ?? null,
            hoursByDate: span?.hoursByDate ?? new Map(),
            plannedHoursByDate: span?.plannedHoursByDate ?? span?.hoursByDate ?? new Map(),
          };
        });

      const featureSpan = featureSpans.get(feature.id) ?? null;

      return {
        featureId: feature.id,
        featureName: feature.name,
        employeeRows,
        loadStartIso: featureSpan?.startIso ?? null,
        loadEndIso: featureSpan?.endIso ?? null,
      };
    });
}
// END_METHOD_buildTimelineRows

// START_METHOD_filterCompletedTimelineRows
// START_CONTRACT: filterCompletedTimelineRows
//   PURPOSE: Скрывает feature-строки с датой завершения строго раньше todayIso (UC-019).
//   INPUTS: rows TimelineRow[], hideCompleted boolean, todayIso YYYY-MM-DD.
//   OUTPUTS: filtered TimelineRow[] (или исходный массив при hideCompleted=false).
//   SIDE_EFFECTS: console.debug branch markers BLOCK_FILTER_APPLIED / BLOCK_FILTER_SKIPPED.
//   LINKS: V-M-FE-TIMELINE, UC-019, DF-TIMELINE-HIDE-COMPLETED
// END_CONTRACT: filterCompletedTimelineRows
export function filterCompletedTimelineRows(rows, hideCompleted, todayIso) {
  /* START_BLOCK_FILTER_SKIPPED */
  if (!hideCompleted) {
    if (typeof console !== 'undefined') {
      console.debug('[timelineUtils][filterCompletedTimelineRows][BLOCK_FILTER_SKIPPED]', {
        rowCount: rows?.length ?? 0,
      });
    }
    return rows ?? [];
  }
  /* END_BLOCK_FILTER_SKIPPED */

  /* START_BLOCK_FILTER_APPLIED */
  const filtered = (rows ?? []).filter((row) => {
    if (!row?.loadEndIso) {
      return true;
    }
    return row.loadEndIso >= todayIso;
  });
  if (typeof console !== 'undefined') {
    console.debug('[timelineUtils][filterCompletedTimelineRows][BLOCK_FILTER_APPLIED]', {
      before: rows?.length ?? 0,
      after: filtered.length,
      todayIso,
    });
  }
  return filtered;
  /* END_BLOCK_FILTER_APPLIED */
}
// END_METHOD_filterCompletedTimelineRows

// START_METHOD_formatHours
// START_CONTRACT: formatHours
//   PURPOSE: Форматирует часы для UI label.
//   INPUTS: hours number|null.
//   OUTPUTS: string e.g. "8 ч" or "".
//   SIDE_EFFECTS: none.
//   LINKS: M-FE-TIMELINE
// END_CONTRACT: formatHours
export function formatHours(hours) {
  if (hours == null) {
    return '';
  }
  const rounded = Math.round(hours * 100) / 100;
  return `${rounded} ч`;
}
// END_METHOD_formatHours

// START_METHOD_formatDayHours
// START_CONTRACT: formatDayHours
//   PURPOSE: Форматирует часы дня без суффикса.
//   INPUTS: hours number|null.
//   OUTPUTS: string.
//   SIDE_EFFECTS: none.
//   LINKS: M-FE-TIMELINE
// END_CONTRACT: formatDayHours
export function formatDayHours(hours) {
  if (hours == null) {
    return '';
  }
  const rounded = Math.round(hours * 100) / 100;
  return String(rounded);
}
// END_METHOD_formatDayHours

// START_METHOD_overlapsVisibleWindow
// START_CONTRACT: overlapsVisibleWindow
//   PURPOSE: Проверяет пересечение load span row с visible window.
//   INPUTS: row, visibleStartIso, visibleEndIso.
//   OUTPUTS: boolean.
//   SIDE_EFFECTS: none.
//   LINKS: V-M-FE-TIMELINE
// END_CONTRACT: overlapsVisibleWindow
export function overlapsVisibleWindow(row, visibleStartIso, visibleEndIso) {
  if (!row?.loadStartIso || !row?.loadEndIso || !visibleStartIso || !visibleEndIso) {
    return false;
  }
  return row.loadStartIso <= visibleEndIso && row.loadEndIso >= visibleStartIso;
}
// END_METHOD_overlapsVisibleWindow

// START_METHOD_isSameTimelineParticipant
// START_CONTRACT: isSameTimelineParticipant
//   PURPOSE: Сравнивает участников timeline row (employeeId или projectRoleId).
//   INPUTS: a, b row participant objects.
//   OUTPUTS: boolean.
//   SIDE_EFFECTS: none.
//   LINKS: M-FE-TIMELINE
// END_CONTRACT: isSameTimelineParticipant
export function isSameTimelineParticipant(a, b) {
  if (!a || !b) {
    return false;
  }
  if (a.employeeId != null && b.employeeId != null) {
    return a.employeeId === b.employeeId;
  }
  return a.projectRoleId === b.projectRoleId;
}
// END_METHOD_isSameTimelineParticipant

// START_METHOD_isIsoWithinInclusiveSpan
// START_CONTRACT: isIsoWithinInclusiveSpan
//   PURPOSE: Проверяет, попадает ли ISO-дата в закрытый интервал span.
//   INPUTS: iso string, startIso string|null, endIso string|null.
//   OUTPUTS: boolean.
//   SIDE_EFFECTS: none.
//   LINKS: M-FE-TIMELINE, UC-017
// END_CONTRACT: isIsoWithinInclusiveSpan
export function isIsoWithinInclusiveSpan(iso, startIso, endIso) {
  if (!iso || !startIso || !endIso) {
    return false;
  }
  return iso >= startIso && iso <= endIso;
}
// END_METHOD_isIsoWithinInclusiveSpan

// START_METHOD_formatFeatureSpanRange
// START_CONTRACT: formatFeatureSpanRange
//   PURPOSE: Форматирует подпись диапазона дат задачи для режима сводки.
//   INPUTS: startIso string|null, endIso string|null.
//   OUTPUTS: string — «9 июл» или «9 июл — 16 июл», либо пустая строка.
//   SIDE_EFFECTS: none.
//   LINKS: M-FE-TIMELINE, UC-017
// END_CONTRACT: formatFeatureSpanRange
export function formatFeatureSpanRange(startIso, endIso) {
  if (!startIso || !endIso) {
    return '';
  }
  const start = parseIsoDateLocal(startIso);
  const end = parseIsoDateLocal(endIso);
  if (!start || !end) {
    return '';
  }
  if (startIso === endIso) {
    return formatDayMonth(start);
  }
  return `${formatDayMonth(start)} — ${formatDayMonth(end)}`;
}
// END_METHOD_formatFeatureSpanRange
